/**
 * Startseite als One-Pager mit Section-Registry.
 *
 * Die Seite ist ein WERKZEUG, keine Broschüre: kein Hero, kein
 * Nutzenversprechen-Satz, keine Bühne. Sie erklärt sich durch ihre Daten.
 * Dramaturgie von oben nach unten:
 *
 *   Wo bin ich → was ist passiert → was passiert gerade → wer macht das →
 *   wann kann ich hin → wem glaube ich das
 *
 * Die Seite liest vorbereitete Dokumente — Verzeichnisse (Gebiete, Parteien,
 * Wahlen, Gremien, Fraktionen, Datenstand) und den Aufmacher des gemerkten
 * Gebiets. Auswahl und Reihenfolge entstehen im Export der Pipeline, nicht
 * hier; der Satz dazu steht in `sections/auswahl-texte.ts`.
 *
 * Pattern:
 *   - Pages dispatchen nur Actions — keine Service-Calls.
 *   - Verzeichnisse einmal je Besuch (`fireOnce`), der Aufmacher bei jedem
 *     Wechsel des gemerkten Gebiets: Sonst zöge ein Gebietswechsel 152 KB
 *     Gebietsverzeichnis erneut nach. Deshalb bleibt `sectionActions`
 *     ungenutzt, bis Blöcke eigene Calls haben.
 *   - Je Block ein Selector; die Blöcke bekommen ihren Ausschnitt als Property
 *     und kennen die Herkunft nicht.
 *   - Die `<section>`-Elemente stehen IMMER, auch ohne Daten: Sie sind die
 *     Sprungziele des Menüs, und angemeldet werden sie einmal beim ersten
 *     Rendern. Nur ihr Inhalt hängt an den Daten.
 *   - Der Merker ist Sache dieser Seite, nicht des Stores: Er liegt im
 *     Browser, nicht im Bestand. Die Blöcke MELDEN nur (`gebiet-merken`,
 *     `gebiet-loeschen`) — geschrieben wird an einer Stelle.
 *   - Ein gemerktes Gebiet wechselt die Seite NICHT. Die gefilterte Startseite
 *     ist die schmale Sicht (dieselben vier Blöcke, nur enger); das
 *     Veedel-Profil ist die tiefe Sicht und wird ausdrücklich angesteuert.
 *     NavigationConfig.pages[].name, `sections`-Keys zu den `sec`-Werten der
 *     MenuElement-Einträge in navigation.ts. Einstieg (Block 0) und Quellen
 *     haben KEINEN Menüpunkt — das Menü sind die vier Themenblöcke.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { Subscription } from "rxjs";

import { fireOnceService, pdStore } from "@progressive-development/pd-spa-helper";
import type { startseite } from "@progressive-development/politik-model";

import {
  ROUTE_HOME,
  SEC_EINSTIEG,
  SEC_WAHLEN,
  SEC_ENTSCHEIDUNGEN,
  SEC_GREMIEN,
  SEC_TERMINE,
  SEC_QUELLEN,
} from "../../../../config/navigation.js";

import {
  leseGebietsAuswahl,
  loescheGebietsAuswahl,
  speichereGebietsAuswahl,
  type GebietsAuswahl,
} from "../../../../config/gebiet-auswahl.js";
import { loadAufmacher, loadVerzeichnisse } from "../../store/startseite-actions.js";
import { selectAufmacher, selectVerzeichnisse } from "../../store/startseite-selector.js";
import type { StartseiteVerzeichnisse } from "../../service/startseite-service.js";
import {
  aufmacherSlug,
  gebietFuerMerker,
  bezirkName,
  gemerkterStadtteil,
} from "../../startseite-bezug.js";
import { GEBIET_LOESCHEN, GEBIET_MERKEN } from "../../../../common/components/block-parts.js";

// Sections eager importieren — sie sind die Seite, nicht ihr Beiwerk.
import "./sections/app-einstieg-block.js";
import "../../../wahlen/sections/app-wahl-gebietsblock.js";
import "./sections/app-entscheidungen-block.js";
import "./sections/app-gremien-block.js";
import "./sections/app-termine-block.js";
import "./sections/app-quellen-block.js";
import "../../../../common/components/app-seitenkopf.js";
import "../../../../common/components/app-abschnitte.js";
import { blockSeitenStyles } from "../../../../common/styles/block-seiten-styles.js";

@customElement("app-default-home")
@localized()
export class AppDefaultHome extends LitElement {
  /** undefined = noch nicht geladen; der Ladezustand zeigt der zentrale Indikator. */
  @state() private _verzeichnisse?: StartseiteVerzeichnisse;
  @state() private _aufmacher?: startseite.AufmacherDokument;

  /** Das gemerkte Gebiet — aus dem Browser gelesen, nicht aus dem Bestand. */
  @state() private _merker: GebietsAuswahl | undefined = leseGebietsAuswahl();

  private _subscriptions: Subscription[] = [];

  static override styles = [
    blockSeitenStyles,
    css`
      :host {
        display: block;
        background: var(--portal-papier);
        padding-bottom: var(--portal-block-gap);
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();

    // Verzeichnisse: einmal je Sitzung. Sie ändern sich höchstens jährlich.
    fireOnceService.fireOnce("startseite:verzeichnisse", loadVerzeichnisse.action());
    // Aufmacher: bei jedem Betreten, weil sich der Merker zwischendurch
    // geändert haben kann (Profil, Veedel-Seite, anderer Tab).
    pdStore().dispatch(loadAufmacher.action(aufmacherSlug(this._merker)));

    this._subscriptions = [
      pdStore()
        .select(selectVerzeichnisse)
        .subscribe((v) => {
          this._verzeichnisse = v;
        }),
      pdStore()
        .select(selectAufmacher)
        .subscribe((a) => {
          this._aufmacher = a;
        }),
    ];

    this.addEventListener(GEBIET_MERKEN, this._merken as EventListener);
    this.addEventListener(GEBIET_LOESCHEN, this._merkerLoeschen);
  }

  override disconnectedCallback(): void {
    this.removeEventListener(GEBIET_MERKEN, this._merken as EventListener);
    this.removeEventListener(GEBIET_LOESCHEN, this._merkerLoeschen);
    this._subscriptions.forEach((s) => s.unsubscribe());
    this._subscriptions = [];
    super.disconnectedCallback();
  }

  /**
   * Ein Block meldet, dass jemand ein Gebiet gemerkt hat.
   *
   * Geschrieben wird NUR hier: Der Merker steht im Browser, und zwei Elemente,
   * die beide schreiben, laufen früher oder später auseinander. Die Blöcke
   * bekommen den neuen Stand als Property zurück.
   */
  private _merken = (e: CustomEvent<GebietsAuswahl>): void => {
    if (!e.detail) return;
    speichereGebietsAuswahl(e.detail);
    this._setzeMerker(e.detail);
  };

  /** Der Rückweg zur stadtweiten Sicht — aus der Leiste oder aus jedem Block. */
  private _merkerLoeschen = (): void => {
    loescheGebietsAuswahl();
    this._setzeMerker(undefined);
  };

  private _setzeMerker(merker: GebietsAuswahl | undefined): void {
    this._merker = merker;
    pdStore().dispatch(loadAufmacher.action(aufmacherSlug(merker)));
  }

  override render() {
    const v = this._verzeichnisse;
    const gebiet = v ? gebietFuerMerker(v.gebiete, this._merker) : undefined;
    const ort = v ? bezirkName(v.gebiete, this._merker) : "";
    // Nur gesetzt, wenn ein VEEDEL gemerkt ist — der Block zeigt den Bezirk
    // und bietet damit den Weg zur engeren Sicht an.
    const veedel = v ? gemerkterStadtteil(v.gebiete, this._merker) : undefined;

    return html`
      <!--
        Einfassung oben, kein Titel: Die Startseite hat bewusst keine
        Überschrift. Ein Element im Pfad heißt „kein Link" — es sagt nur, wo
        man ist, und wird auf den Tiefenseiten zum Weg zurück.

        Hier steht „Startseite" und NICHT der Portalname: Der Pfad sagt, wo man
        ist, nicht wie das Angebot heißt. Den Namen trägt seit der Bildmarke das
        Logo eine Zeile darüber — er stünde sonst zweimal untereinander.
      -->
      <app-seitenkopf
        .pfad=${[{ name: msg("Startseite", { id: "app.home.pfad" }) }]}
      ></app-seitenkopf>
      <app-abschnitte
        .abschnitte=${[
          { id: SEC_EINSTIEG, name: msg("Bezirke und Veedel", { id: "app.abschnitt.einstieg" }) },
          { id: SEC_WAHLEN, name: msg("Wahlen", { id: "app.menu.wahlen" }) },
          {
            id: SEC_ENTSCHEIDUNGEN,
            name: msg("Entscheidungen", { id: "app.menu.entscheidungen" }),
          },
          { id: SEC_GREMIEN, name: msg("Gremien", { id: "app.menu.gremien" }) },
          { id: SEC_TERMINE, name: msg("Termine", { id: "app.menu.termine" }) },
        ]}
      ></app-abschnitte>
      <!--
        DIE SECTIONS STEHEN IMMER, ihr Inhalt kommt mit den Daten: Sie sind
        die Ziele der Abschnittsleiste, und die findet nur, was im DOM steht.
      -->
      <section id=${SEC_EINSTIEG}>
        ${
          v
            ? html`<app-einstieg-block
                .gebiete=${v.gebiete}
                .merker=${this._merker}
              ></app-einstieg-block>`
            : nothing
        }
      </section>
      <section id=${SEC_WAHLEN}>
        <!-- Mit gemerktem Gebiet zusätzlich die Bezirksvertretung: die örtlichste
             aller Wahlen, und wer sein Veedel gemerkt hat, hat danach gefragt.
             Ohne Merker steht hier die ganze Stadt, und eine BV der Stadt gibt
             es nicht. Die Verweiszeile bleibt kommunal (Vorgabe des Bauteils);
             Bund und Europa stehen im Kapitel, das sie auch erklärt. -->
        ${
          v && gebiet
            ? html`<app-wahl-gebietsblock
                .gebiet=${gebiet}
                .aktuell=${v.aktuelleWahlen}
                .wahlen=${v.wahlen}
                .parteien=${v.parteien}
                .arten=${this._merker ? ["rat", "bezirksvertretung"] : ["rat"]}
                animiert
                ?gefiltert=${!!this._merker}
              ></app-wahl-gebietsblock>`
            : nothing
        }
      </section>
      <section id=${SEC_ENTSCHEIDUNGEN}>
        ${
          v
            ? html`<app-entscheidungen-block
                .aufmacher=${this._aufmacher}
                .parteien=${v.parteien}
                gebietName=${ort}
                stadtteilName=${veedel?.name ?? ""}
                stadtteilSlug=${veedel?.slug ?? ""}
                stadtteilBezirkSlug=${veedel?.bezirkSlug ?? ""}
                ?gefiltert=${!!this._merker}
              ></app-entscheidungen-block>`
            : nothing
        }
      </section>
      <section id=${SEC_GREMIEN}>
        ${
          v
            ? html`<app-gremien-block
                .gremien=${v.gremien}
                .sitzverteilung=${v.sitzverteilung}
                .gremienDerStadt=${v.gremienDerStadt}
                .dezernate=${v.dezernate}
                .parteien=${v.parteien}
                .naechsteBvSitzung=${this._aufmacher?.naechsteBvSitzung}
                .merker=${this._merker}
              ></app-gremien-block>`
            : nothing
        }
      </section>
      <section id=${SEC_TERMINE}>
        ${
          v
            ? html`<app-termine-block
                .termine=${this._aufmacher?.termine ?? []}
                hinweis=${this._aufmacher?.termineHinweis ?? ""}
                gebietName=${ort}
                ?gefiltert=${!!this._merker}
              ></app-termine-block>`
            : nothing
        }
      </section>
      <section id=${SEC_QUELLEN}>
        ${v ? html`<app-quellen-block .datenstand=${v.datenstand}></app-quellen-block>` : nothing}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-default-home": AppDefaultHome;
  }
}
